import {
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, concat } from 'rxjs';
import {
  InterceptorFn,
  InterceptorFnResult,
  OnInterceptParams,
  shellWindow
} from '../globals/globals';

/**
 * This interceptor will call the registered interceptors of the MFEs.
 * Each MFE can register/unregister its interceptors using the shellWindow.registerInterceptor function.
 */
@Injectable()
export class RootInterceptor implements HttpInterceptor {
  /**
   * We can't inject this as dependency because it'll lead to a circular dependency error with HttpClient
   *
   * There were two major problems with the old system.
   * First, the modified request from the intermediate interceptors are not taken into account when passing to the root handler.
   * Second, the root handler is passed to the intermediate interceptors while we're also manually calling the root handler.
   */
  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): InterceptorFnResult {
    // TODO Remove the old logic once all the MFEs are migrated to the new system
    const nextHandler: HttpHandler = {
      handle: () => {
        return EMPTY;
      }
    };
    const params: OnInterceptParams = {
      interceptors: [],
      request,
      next: nextHandler
    };
    shellWindow.mfeShell!.onIntercept.next(params); // eslint-disable-line @typescript-eslint/no-non-null-assertion

    const result = this.processInterceptors(
      shellWindow.mfeShell!.getInterceptors(), // eslint-disable-line @typescript-eslint/no-non-null-assertion
      request,
      next
    );
    if (params.interceptors.length) {
      return concat(...params.interceptors, result) as any;
    } else {
      return result;
    }
  }

  // The old system was using the original request instead of the
  private processInterceptors(
    interceptors: InterceptorFn[],
    request: HttpRequest<unknown>,
    handler: HttpHandler
  ): InterceptorFnResult {
    if (interceptors.length === 0) {
      return handler.handle(request);
    } else {
      const currentInterceptor = interceptors[0];
      const nextHandler: HttpHandler = {
        handle: (nextRequest: HttpRequest<unknown>) => {
          return this.processInterceptors(
            interceptors.slice(1),
            nextRequest,
            handler
          );
        }
      };
      return currentInterceptor(request, nextHandler);
    }
  }
}
